import { ChatEvaluationTestResult } from "@/API";
import { Badge, Divider, Group, Paper, Stack, Tabs, Text } from "@mantine/core";
import { EvaluationMetricBadges } from "./MetricBadge";
import { formatDurationSec, getAggregatedDurations, getAggregatedMetrics, MetricAggregationType } from "./EvaluationHelpers";

export interface TestResultDetailModalContentProps {
    record: ChatEvaluationTestResult;
    initialRunIndex?: number | "default";
    globalMetricMode?: MetricAggregationType;
}

export function TestResultDetailModalContent({
    record,
    initialRunIndex = "default",
    globalMetricMode = "avg",
}: TestResultDetailModalContentProps) {
    const executions = record.executionResults && record.executionResults.length > 0
        ? record.executionResults
        : [];

    const modeLabel = globalMetricMode === "avg" ? "平均" : globalMetricMode === "max" ? "最高" : "最低";

    return (
        <Stack gap="md">
            <Stack gap={4}>
                <Text fw={600} size="sm">使用者輸入：</Text>
                <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{record.sample?.userInput}</Text>
            </Stack>
            <Stack gap={4}>
                <Text fw={600} size="sm">標準回應：</Text>
                <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{record.sample?.reference}</Text>
            </Stack>
            <Divider />

            {executions.length > 1 ? (
                <Tabs defaultValue={typeof initialRunIndex === "number" ? `run-${initialRunIndex}` : "default"}>
                    <Tabs.List mb="xs">
                        <Tabs.Tab value="default">預設 ({modeLabel})</Tabs.Tab>
                        {executions.map((_, idx) => (
                            <Tabs.Tab key={idx} value={`run-${idx}`}>
                                測試 #{idx + 1}
                            </Tabs.Tab>
                        ))}
                    </Tabs.List>

                    <Tabs.Panel value="default">
                        <Stack gap="sm">
                            <Group align="center">
                                <Text fw={600} size="sm">{modeLabel}評估指標：</Text>
                                <EvaluationMetricBadges metricsList={[getAggregatedMetrics(executions, globalMetricMode)]} />
                            </Group>

                            {(() => {
                                const defaultDurations = getAggregatedDurations(executions, globalMetricMode);
                                return (
                                    <Group align="center" gap="xs">
                                        <Text fw={600} size="sm">{modeLabel}回應時間：</Text>
                                        <Badge variant="outline" color="blue" size="sm">對話: {formatDurationSec(defaultDurations.chatDurationMs)}</Badge>
                                        <Badge variant="outline" color="teal" size="sm">評估: {formatDurationSec(defaultDurations.evaluationDurationMs)}</Badge>
                                    </Group>
                                );
                            })()}

                            <Stack gap={4}>
                                <Group gap="xs" align="center">
                                    <Text fw={600} size="sm">AI 回應：</Text>
                                    <Badge size="xs" variant="light" color="gray">以第 1 次測試為例</Badge>
                                </Group>
                                <Paper p="xs" withBorder bg="var(--mantine-color-body)">
                                    <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{executions[0]?.response || "無回應"}</Text>
                                </Paper>
                            </Stack>

                            <Stack gap={4}>
                                <Text fw={600} size="sm">擷取上下文 ({executions[0]?.retrievedContexts?.length ?? 0} 筆)：</Text>
                                {(!executions[0]?.retrievedContexts || executions[0].retrievedContexts.length === 0) ? (
                                    <Text size="xs" c="dimmed">無擷取上下文</Text>
                                ) : (
                                    executions[0].retrievedContexts.map((ctx: string, cIdx: number) => (
                                        <Paper p="xs" key={cIdx} withBorder radius="xs">
                                            <Text size="xs" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{ctx}</Text>
                                        </Paper>
                                    ))
                                )}
                            </Stack>
                        </Stack>
                    </Tabs.Panel>

                    {executions.map((run, idx) => (
                        <Tabs.Panel key={idx} value={`run-${idx}`}>
                            <Stack gap="sm">
                                {run.metrics && (
                                    <Group align="center">
                                        <Text fw={600} size="sm">當次評估指標：</Text>
                                        <EvaluationMetricBadges metricsList={[run.metrics]} />
                                    </Group>
                                )}

                                <Group align="center" gap="xs">
                                    <Text fw={600} size="sm">當次回應時間：</Text>
                                    <Badge variant="outline" color="blue" size="sm">對話: {formatDurationSec(run.chatDurationMs)}</Badge>
                                    <Badge variant="outline" color="teal" size="sm">評估: {formatDurationSec(run.evaluationDurationMs)}</Badge>
                                </Group>

                                <Stack gap={4}>
                                    <Text fw={600} size="sm">AI 回應：</Text>
                                    <Paper p="xs" withBorder bg="var(--mantine-color-body)">
                                        <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{run.response || "無回應"}</Text>
                                    </Paper>
                                </Stack>

                                <Stack gap={4}>
                                    <Text fw={600} size="sm">擷取上下文 ({run.retrievedContexts?.length ?? 0} 筆)：</Text>
                                    {(!run.retrievedContexts || run.retrievedContexts.length === 0) ? (
                                        <Text size="xs" c="dimmed">無擷取上下文</Text>
                                    ) : (
                                        run.retrievedContexts.map((ctx: string, cIdx: number) => (
                                            <Paper p="xs" key={cIdx} withBorder radius="xs">
                                                <Text size="xs" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{ctx}</Text>
                                            </Paper>
                                        ))
                                    )}
                                </Stack>
                            </Stack>
                        </Tabs.Panel>
                    ))}
                </Tabs>
            ) : (
                <Stack gap="sm">
                    {executions[0]?.metrics && (
                        <Group align="center">
                            <Text fw={600} size="sm">評估指標：</Text>
                            <EvaluationMetricBadges metricsList={[executions[0].metrics]} />
                        </Group>
                    )}

                    <Group align="center" gap="xs">
                        <Text fw={600} size="sm">回應時間：</Text>
                        <Badge variant="outline" color="blue" size="sm">對話: {formatDurationSec(executions[0]?.chatDurationMs)}</Badge>
                        <Badge variant="outline" color="teal" size="sm">評估: {formatDurationSec(executions[0]?.evaluationDurationMs)}</Badge>
                    </Group>

                    <Stack gap={4}>
                        <Text fw={600} size="sm">AI 回應：</Text>
                        <Paper p="xs" withBorder bg="var(--mantine-color-body)">
                            <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{executions[0]?.response || "無回應"}</Text>
                        </Paper>
                    </Stack>

                    <Stack gap={4}>
                        <Text fw={600} size="sm">擷取上下文 ({executions[0]?.retrievedContexts?.length ?? 0} 筆)：</Text>
                        {(!executions[0]?.retrievedContexts || executions[0].retrievedContexts.length === 0) ? (
                            <Text size="xs" c="dimmed">無擷取上下文</Text>
                        ) : (
                            executions[0].retrievedContexts.map((ctx: string, cIdx: number) => (
                                <Paper p="xs" key={cIdx} withBorder radius="xs">
                                    <Text size="xs" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{ctx}</Text>
                                </Paper>
                            ))
                        )}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}
